import { faMagnifyingGlass } from "@fortawesome/pro-light-svg-icons"
import { faCloudUpload, faFile, faTrashCanXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import CloseIcon from "@mui/icons-material/Close"
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  ListItem,
  ListItemText,
  Modal,
  Typography,
  useTheme
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { useApiDelete } from "auth/useApi2"
import { apiEndpoints } from "generated/apiEndpoints"
import { useEffect, useMemo, useState } from "react"
import { useDropzone } from "react-dropzone"

import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardActions from "@mui/material/CardActions"
import CardMedia from "@mui/material/CardMedia"
import theme from "css/theme/light"
import { useTranslation } from "react-i18next"

const MaterialDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(0, 2, 1, 2)
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1)
  },
  "& .MuiPaper-root": {
    maxWidth: "70%",
    borderRadius: "12px",
    [theme.breakpoints.between("xs", "md")]: {
      margin: 0,
      maxWidth: "80%"
    }
  },
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(0, 0, 0, 0.30)"
  }
}))

const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "24px",
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.secondary.main,
  outline: "none",
  transition: "background .24s ease-in-out",
  cursor: "pointer",
  margin: "0",
  borderRadius: 12,
  textAlign: "center"
}

const activeStyle = {
  backgroundColor: "rgba(233,233,233, 1)"
}

const acceptStyle = {
  backgroundColor: "rgba(233,233,233, 1)"
}

const rejectStyle = {
  borderColor: theme.palette.error.main
}

const StyledBox = styled(Box)(({ theme }) => ({
  position: "relative",
  borderWidth: 2,
  borderColor: theme.palette.secondary["600"],
  borderStyle: "dashed",
  borderRadius: 12
}))

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: "rgba(0, 0, 0, 0.54)",
  top: "-11px",
  left: "9px",
  padding: "0 5px",
  background: theme.palette.background.paper,
  position: "absolute"
}))

const StyledParagraph = styled("p")(({ theme }) => ({
  color: theme.palette.secondary.main,
  fontSize: theme.typography.bodyMedium,
  margin: "12px 0 0 0"
}))

const StyledGrid = styled(Grid)(({ theme, parentData }) => ({
  "&.MuiGrid-item": {
    minWidth: parentData ? "min-content" : "100%"
  }
}))

const StyledDeleteButton = styled(Button)(({ theme }) => ({
  color: theme.palette.error.main,
  padding: 0,
  height: "auto",
  "&:hover": {
    backgroundColor: "transparent"
  }
}))

const StyledImg = styled("img")(({ theme }) => ({
  maxWidth: "100%"
}))

const StyledUploadedFileWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 20px",
  backgroundColor: theme.palette.primary[300],
  borderRadius: 12
}))

const MultipleFilePickerPreviewList = ({
  setFieldValue,
  fieldName,
  defaultPreviewFiles,
  setFiles,
  files,
  onLogoChanged,
  label,
  labelInBox,
  fileUploadResult,
  parentData,
  editMode
}) => {
  const [deleteResult, deleteFile, resetDeleteState] = useApiDelete()
  const [open, setOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState("")
  const theme = useTheme()
  const { t } = useTranslation()

  const handleOpen = (previewFile) => {
    setOpen(true)
    setSelectedImage(previewFile)
  }
  const handleClose = () => {
    setOpen(false)
    setSelectedImage("")
  }

  const [previewFiles, setPreviewFiles] = useState(defaultPreviewFiles)
  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    multiple: true,
    paramName: () => "files",
    onDrop: (acceptedFiles) => {
      setFiles(acceptedFiles)
    }
  })
  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {})
    }),
    [isDragActive, isDragReject, isDragAccept]
  )

  const removeFile =
    (index, { fileId, id }) =>
    () => {
      deleteFile(apiEndpoints.issuereports.deletefile.replace("{id}", id))
      const newPreviewFiles = [...previewFiles]
      newPreviewFiles.splice(index, 1)
      setPreviewFiles(newPreviewFiles)

      removeFormValues(index)
    }
  const removeFormValues = (index) => () => {
    const newFiles = [...files]
    newFiles.splice(index, 1)
    setFiles(newFiles)
    setFieldValue(fieldName, newFiles)
  }

  useEffect(() => {
    if (fileUploadResult.status === 2) {
      setPreviewFiles(() => [...previewFiles, fileUploadResult.data])
    }
  }, [fileUploadResult])

  useEffect(() => {
    if (previewFiles) {
      setFieldValue(fieldName, previewFiles)
    }
  }, [previewFiles, onLogoChanged, setFieldValue, fieldName])

  return (
    <>
      {editMode ? (
        <StyledBox border={1} borderColor="secondary.600" borderRadius="4px">
          <StyledTypography variant="caption">{label}</StyledTypography>
          <Box spacing={3}>
            <div {...getRootProps({ style })}>
              <input {...getInputProps()} />
              <FontAwesomeIcon icon={faCloudUpload} color={theme.palette.secondary["600"]} size="2x" />
              <StyledParagraph>{labelInBox}</StyledParagraph>
            </div>
          </Box>
        </StyledBox>
      ) : !parentData ? (
        <StyledBox border={1} borderColor="secondary.600" borderRadius="4px">
          <StyledTypography variant="caption">{label}</StyledTypography>
          <Box spacing={3}>
            <div {...getRootProps({ style })}>
              <input {...getInputProps()} />
              <FontAwesomeIcon icon={faCloudUpload} color={theme.palette.secondary["600"]} size="2x" />
              <StyledParagraph>{labelInBox}</StyledParagraph>
            </div>
          </Box>
        </StyledBox>
      ) : (
        <></>
      )}
      <Grid container direction="row" marginTop={previewFiles && previewFiles.length > 0 ? 2 : 0} spacing={2}>
        {previewFiles &&
          previewFiles.length > 0 &&
          previewFiles.map((previewFile, index) => (
            <StyledGrid item xs={12} md={6} xl={3} key={index} parentData={parentData}>
              <Card>
                {previewFile.base64 ? (
                  <>
                    <CardMedia
                      component="img"
                      sx={{ height: 150 }}
                      alt={`Bild nr ${index + 1}`}
                      image={`data:image/png;base64,${previewFile.base64}`}
                      onClick={() => handleOpen(previewFile)}
                    />
                    <CardActions sx={{ justifyContent: "center" }}>
                      <Button size="small" onClick={() => handleOpen(previewFile)}>
                        {" "}
                        <FontAwesomeIcon
                          icon={faMagnifyingGlass}
                          style={{ color: theme.palette.secondary.main, marginRight: "5px" }}
                        />{" "}
                        <Typography>{t("global.open", "Open")}</Typography>
                      </Button>
                      {editMode ? (
                        <StyledDeleteButton size="medium" onClick={removeFile(index, previewFile)}>
                          <FontAwesomeIcon
                            icon={faTrashCanXmark}
                            style={{ marginRight: "5px" }}
                            color={theme.palette.error.main}
                          />
                          <Typography fontWeight="600" variant="bodyMedium">
                            {t("global.delete", "Delete")}
                          </Typography>
                        </StyledDeleteButton>
                      ) : !parentData ? (
                        <StyledDeleteButton size="medium" onClick={removeFile(index, previewFile)}>
                          <FontAwesomeIcon
                            icon={faTrashCanXmark}
                            style={{ marginRight: "5px" }}
                            color={theme.palette.error.main}
                          />
                          <Typography fontWeight="600" variant="bodyMedium">
                            {t("global.delete", "Delete")}
                          </Typography>
                        </StyledDeleteButton>
                      ) : (
                        <></>
                      )}
                    </CardActions>
                  </>
                ) : (
                  <StyledUploadedFileWrapper>
                    <Box p={0} display="flex" alignItems="center" key={index}>
                      <FontAwesomeIcon
                        size="1x"
                        icon={faFile}
                        style={{ color: theme.palette.primary[700] }}
                      />{" "}
                      <Typography
                        ml={1}
                        variant="bodyMedium"
                        fontWeight="600"
                        color={theme.palette.grey[500]}>
                        {previewFile.name}
                      </Typography>
                    </Box>

                    <StyledDeleteButton size="medium" onClick={removeFile(index, previewFile)}>
                      <FontAwesomeIcon
                        icon={faTrashCanXmark}
                        style={{ marginRight: "5px" }}
                        color={theme.palette.error.main}
                      />
                      <Typography fontWeight="600" variant="bodyMedium">
                        {t("global.delete", "Delete")}
                      </Typography>
                    </StyledDeleteButton>
                  </StyledUploadedFileWrapper>
                )}
              </Card>
            </StyledGrid>
          ))}

        <Modal open={open} onClose={() => handleClose()} aria-labelledby="image">
          <MaterialDialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open}>
            <Grid container justifyContent={"space-between"} alignItems={"center"} flexWrap={"nowrap"}>
              <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                {selectedImage.name}
              </DialogTitle>
              <Button
                aria-label="close"
                onClick={handleClose}
                color="primary"
                variant="outlined"
                sx={{
                  mr: 2
                }}
                startIcon={
                  <CloseIcon
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      borderRadius: "10px"
                    }}
                  />
                }>
                {t("global.close", "Close")}
              </Button>
            </Grid>

            <DialogContent>
              <StyledImg
                src={`data:image/jpeg;base64,${selectedImage.base64}`}
                alt={`${selectedImage.name}`}
                onClick={() => handleClose()}
              />
            </DialogContent>
          </MaterialDialog>
        </Modal>
      </Grid>
    </>
  )
}
export default MultipleFilePickerPreviewList
